import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AddressType } from '@app/core/types/address-type';

/**
 * Service that provides functionality for interacting with an external API to perform
 * address-related operations such as searching for addresses, sanitizing addresses,
 * and retrieving address details by ID.
 *
 * This service is provided globally (root level) and handles HTTP requests through the
 * Angular `HttpClient`.
 */
@Injectable({
  providedIn: 'root'
})
export class AutocompleteService {
  /** Base API root URL */
  private readonly rootUrl: string = 'https://api.dataforsyningen.dk/';

  /** Endpoint for address autocomplete suggestions */
  private readonly addressUrl: string = `${this.rootUrl}adresser/autocomplete/`;
  private readonly accessAddressUrl: string = `${this.rootUrl}adgangsadresser/autocomplete/`;

  /** Restricts results to a specific region (1081 = Nordjylland) */
  // private readonly limiter: string = '&regionskode=1081';
  private readonly limiter: string = '';

  constructor(
    private readonly http: HttpClient,
  ) {}

  /**
   * Searches for address suggestions based on partial input.
   *
   * @param addr - The partial address input from the user.
   * @param type The type of address to search for ('address' or 'access'). Defaults to 'address'.
   * @returns An Observable emitting autocomplete suggestions.
   */
  public searchAddress(addr: string, type: AddressType = 'address'): Observable<any> {
    const url = type === 'access' ? this.accessAddressUrl : this.addressUrl;
    return this.http.get(url + '?q=' + addr + this.limiter);
  }

  /**
   * Sends a request to sanitize and validate a full address string.
   *
   * @param addr - The full address string to sanitize.
   * @returns An Observable emitting the sanitized address data.
   */
  public sanitizeAddress(addr: string): Observable<any> {
    return this.http.get(`${this.rootUrl}datavask/adresser/?betegnelse=${addr}`);
  }

  /**
   * Retrieves detailed address information by its unique ID.
   *
   * @param id - The unique address ID.
   * @param type The type of address to retrieve ('address' or 'access'). Defaults to 'address'.
   * @returns An Observable emitting the address data.
   */
  public getById(id: string, type: AddressType = 'address'): Observable<any> {
    const endpoint = type === 'access' ? 'adgangsadresser' : 'adresser';
    return this.http.get(`${this.rootUrl}${endpoint}/${id}`);
  }
}
